(function (scope) {
  'use strict';
  // Use exactly one existing analytics transport. Tracking must never block cart work.
  function trackEvent(name, payload) {
    try {
      const privacy = scope.Shopify?.customerPrivacy;
      if (typeof privacy?.analyticsProcessingAllowed === 'function' && !privacy.analyticsProcessingAllowed()) return false;
      if (typeof scope.KMTrackGaEventAsync === 'function') {
        const pending = scope.KMTrackGaEventAsync(name, payload, undefined, { timeout: 800, transport: 'beacon' });
        pending?.catch?.(() => {});
      } else if (typeof scope.KMTrackGaEvent === 'function') scope.KMTrackGaEvent(name, payload);
      else if (typeof scope.gtag === 'function') scope.gtag('event', name, payload);
      else { scope.dataLayer = scope.dataLayer || []; scope.dataLayer.push({ ...payload, event: name }); }
      return true;
    } catch (_) { return false; }
  }
  scope.KmFbtAnalytics = { trackEvent };
  const isPurchasable = variant => Boolean(variant && variant.available === true &&
    (variant.inventoryPolicy === 'continue' || variant.tracked !== true || (Number.isFinite(variant.quantity) && variant.quantity > 0)));
  function offerPrice(price, coupon = {}) {
    const basePrice = Number.isFinite(price) ? Math.max(0, Math.round(price)) : 0;
    const raw = Array.isArray(coupon.type) ? (coupon.type.length === 1 ? coupon.type[0] : '') : coupon.type;
    const value = Number(coupon.value);
    const type = ['DISCOUNT_PERCENT', 'DISCOUNT_AMOUNT'].includes(raw) && Number.isFinite(value) && value >= 0 &&
      (raw !== 'DISCOUNT_PERCENT' || value <= 100) ? raw : '';
    const next = type === 'DISCOUNT_PERCENT' ? Math.round(basePrice * (1 - value / 100)) :
      type === 'DISCOUNT_AMOUNT' ? Math.max(0, basePrice - Math.round(value * 100)) : basePrice;
    return { basePrice, price: next, discount: basePrice - next, type, value: type ? value : 0, code: String(coupon.code || '') };
  }
  function resolveVariant(variants, selections) {
    if (!Array.isArray(selections) || selections.some(v => v == null || v === '')) return null;
    return (variants || []).find(v => v.options.length === selections.length && v.options.every((x, i) => x === selections[i])) || null;
  }
  function quotedPrice(price, coupon) {
    return offerPrice(price, coupon?.code && String(coupon.code).trim() ? coupon : {});
  }
  function calculateTotals(lines) {
    let base = 0; let discount = 0;
    const groups = new Map(); const unverifiedCodes = []; const sharedCodes = [];
    for (const line of lines) {
      const quantity = Number.isSafeInteger(line.quantity) && line.quantity > 0 ? line.quantity : 1;
      const price = offerPrice(line.variant?.price).basePrice;
      base += price * quantity;
      const code = String(line.coupon?.code || '').trim();
      if (!code) continue;
      if (!groups.has(code)) groups.set(code, []);
      groups.get(code).push({ price, quantity, coupon: line.coupon });
    }
    for (const [code, group] of groups) {
      const first = group[0].coupon; const offer = offerPrice(0, first);
      const consistent = first.verified === true && offer.type &&
        (offer.type !== 'DISCOUNT_AMOUNT' || typeof first.perItem === 'boolean') &&
        group.every(line => {
          const candidate = offerPrice(0, line.coupon);
          return line.coupon.verified === true && candidate.type === offer.type && candidate.value === offer.value && line.coupon.perItem === first.perItem;
        });
      if (!consistent) { unverifiedCodes.push(code); continue; }
      const groupBase = group.reduce((sum, line) => sum + line.price * line.quantity, 0);
      if (offer.type === 'DISCOUNT_PERCENT') {
        discount += group.reduce((sum, line) => sum + line.price * line.quantity - Math.round(line.price * line.quantity * (1 - offer.value / 100)), 0);
      } else if (first.perItem) {
        discount += group.reduce((sum, line) => sum + Math.min(line.price, Math.round(offer.value * 100)) * line.quantity, 0);
      } else {
        discount += Math.min(groupBase, Math.round(offer.value * 100));
        if (group.reduce((sum, line) => sum + line.quantity, 0) > 1) sharedCodes.push(code);
      }
    }
    return { base, estimated: Math.max(0, base - discount), discount, unverifiedCodes, sharedCodes };
  }
  function freshnessDecision(before, after, quantity = 1) {
    if (!after || !isPurchasable(after.variant) || (after.variant.inventoryPolicy !== 'continue' && after.variant.tracked === true && after.variant.quantity < quantity)) return 'unavailable';
    const oldQuote = quotedPrice(before.variant.price, before.coupon);
    const newQuote = quotedPrice(after.variant.price, after.coupon);
    return oldQuote.basePrice !== newQuote.basePrice || oldQuote.price !== newQuote.price || oldQuote.code !== newQuote.code || oldQuote.type !== newQuote.type || oldQuote.value !== newQuote.value ||
      before.coupon?.verified !== after.coupon?.verified || before.coupon?.perItem !== after.coupon?.perItem ? 'priceChanged' : 'ready';
  }
  function isDefaultOption(option, product) {
    return product.variants.length === 1 && option.values.length === 1 && option.values[0] === 'Default Title';
  }
  // Earlier choices constrain later choices. Changing color can therefore unlock a different size.
  function optionsAvailability(variants, selections, index, value) {
    return (variants || []).some(v => isPurchasable(v) && v.options[index] === value &&
      v.options.every((x, i) => i >= index || !selections[i] || selections[i] === x));
  }
  async function requestData(url, options = {}, kind = 'json', timeout = 10000) {
    const controller = new scope.AbortController();
    let timer;
    try {
      return await Promise.race([
        (async () => {
          const response = await scope.fetch(url, { credentials: 'same-origin', ...options, signal: controller.signal });
          if (!response.ok) {
            const error = new Error('request-rejected'); error.status = response.status; throw error;
          }
          return kind === 'text' ? response.text() : response.json();
        })(),
        new Promise((_, reject) => { timer = scope.setTimeout(() => { controller.abort(); reject(new Error('request-timeout')); }, timeout); })
      ]);
    } finally { scope.clearTimeout(timer); }
  }
  scope.KmFbtRequest = requestData;
  const logic = { offerPrice, quotedPrice, calculateTotals, freshnessDecision, isDefaultOption, isPurchasable, resolveVariant, optionsAvailability };
  scope.KmHeatedFbtLogic = logic;
  if (!scope.customElements || scope.customElements.get('km-heated-fbt')) return;
  const defaults = {
    title: 'Gear Up to Beat the Cold', chooseOptionsEntry: 'Choose options', completeOptions: 'Choose options to continue', selectOption: 'Select', soldOut: 'Sold out', unavailable: 'Unavailable',
    add: 'Add selected items to cart', adding: 'Adding…', chooseOptions: 'Choose all options before adding.',
    error: 'Unable to confirm the addition. Check your cart before trying again.', added: 'Added',
    baseTotal: 'Product total', couponTotal: 'Estimated total after coupons',
    couponNotice: 'Coupon prices are estimates. Eligibility, combinations and final discounts are confirmed at checkout. Coupons are not applied automatically.',
    mainUnavailable: 'The selected main product is unavailable.', couponCodes: 'Coupon codes',
    selectionRequired: 'Select options', offPercent: '{value}% OFF', offAmount: '{value} OFF', estimated: 'With eligible code',
    refreshError: 'Unable to verify current stock. Please try again.', priceChanged: 'Prices have changed. Review your selection before adding again.',
    soloPrice: 'Price when bought alone with code', sharedCouponNotice: 'Fixed coupon savings are shared across eligible selected items.',
    unverifiedCouponNotice: 'Unverified coupon savings are excluded from the estimated total.',
    giftError: 'Items added. Please choose your free gift in the cart.',
    nothingSelected: 'Select an add-on to continue.', checkCart: 'Check cart status',
    uncertain: 'Please review your cart. The previous request could not be fully confirmed. No items will be added again.',
    rejected: 'Some items could not be added. Review your cart and current stock before trying again.'
  };
  class HeatedFbt extends scope.HTMLElement {
    connectedCallback() {
      if (this.initialized) {
        this.form?.addEventListener('change', this.handleChange);
        this.startPoll();
        this.bindPrewarm();
        this.observeView();
        return;
      }
      const source = this.querySelector('script[data-fbt-config]');
      if (!source) return;
      try { this.config = JSON.parse(source.textContent); } catch (_) { return; }
      this.initialized = true;
      this.s = Object.assign({}, defaults, this.config.strings);
      this.money = n => new Intl.NumberFormat(this.config.locale || 'en-US', { style: 'currency', currency: this.config.currency || 'USD' }).format(n / 100);
      const seen = new Set([String(this.config.main.id)]);
      this.items = (this.config.items || []).filter(item => {
        if (!item.product || seen.has(String(item.product.id)) || !item.product.variants?.some(isPurchasable)) return false;
        seen.add(String(item.product.id)); return true;
      }).map(item => {
        const options = item.product.options || [];
        const live = item.product.variants.some(isPurchasable);
        const outfitCategory = ['pants', 'gloves', 'socks', 'shoes'].includes(String(item.category || '').toLowerCase());
        return { ...item, live, selected: live && (item.defaultSelected === true || outfitCategory),
          choices: options.map(o => o.values.length === 1 ? o.values[0] : null) };
      });
      this.form = this.findForm();
      this.restoreAttempt();
      this.render();
      this.prewarmStock = () => { if (!this.busy) this.loadFreshConfig().catch(() => {}); };
      this.bindPrewarm();
      this.handleChange = () => {
        this.update();
        const signature = this.formSignature();
        if (signature !== this.mainSignature) {
          this.mainSignature = signature;
          this.trackMainVariantChange();
        }
      };
      this.form?.addEventListener('change', this.handleChange);
      // Many themes update hidden variant IDs programmatically; this bounded poll reads only this main form.
      this.startPoll();
      this.observeView();
    }
    track(name, item = null, extra = {}) {
      const product = item?.product;
      let selectedHandle = '';
      try { selectedHandle = new URL(product?.url || '', scope.location?.origin).pathname.match(/\/products\/([^/?]+)/)?.[1] || ''; } catch (_) {}
      let sourceHandle = '';
      try { sourceHandle = new URL(this.config.main.url || '', scope.location?.origin).pathname.match(/\/products\/([^/?]+)/)?.[1] || ''; } catch (_) {}
      return trackEvent(name, {
        fitment_module: 'heated_fbt', source_product_id: String(this.config.main.id),
        source_product_handle: sourceHandle, selected_product_id: product ? String(product.id) : '',
        selected_product_handle: selectedHandle, selected_variant_id: item?.variant ? String(item.variant.id) : '',
        selected_available: item ? String(isPurchasable(item.variant) || (!item.variant && item.live === true)) : '',
        item_category: item?.category || '', page_path: scope.location?.pathname || '',
        theme_context: this.config.previewThemeId ? 'preview' : 'live', ...extra
      });
    }
    observeView() {
      if (this.viewTracked || !scope.IntersectionObserver) return;
      this.viewObserver?.disconnect();
      this.viewObserver = new scope.IntersectionObserver(entries => {
        if (entries.some(entry => entry.isIntersecting) && !this.viewTracked) {
          this.viewTracked = this.track('km_fbt_view', null, { item_count: this.items.length });
          if (this.viewTracked) this.viewObserver.disconnect();
        }
      });
      this.viewObserver.observe(this.querySelector('.km-fbt__title') || this);
    }
    startPoll() {
      scope.clearInterval(this.poll);
      this.mainSignature = this.formSignature();
      this.lastMainVariantId = this.mainSignature.split('|')[0];
      this.poll = scope.setInterval(() => {
        if (this.busy) return;
        const signature = this.formSignature();
        if (signature !== this.mainSignature) {
          this.mainSignature = signature;
          this.update();
          this.trackMainVariantChange();
        }
      }, 600);
    }
    formSignature() {
      return `${this.form?.querySelector('[name="id"]')?.value || ''}|${this.form?.elements?.namedItem('quantity')?.value || '1'}`;
    }
    trackMainVariantChange() {
      const id = String(this.mainVariant()?.id || '');
      if (!id || id === this.lastMainVariantId) return;
      this.lastMainVariantId = id;
      this.track('km_fbt_main_variant_selected', null, { selected_variant_id: id, selected_available: String(isPurchasable(this.mainVariant())) });
    }
    recordTiming(phase, started, status = 'ok') {
      const detail = { phase, durationMs: Math.round((scope.performance?.now?.() ?? Date.now()) - started), status };
      scope.console?.debug?.('[km-fbt]', detail);
      document.dispatchEvent(new CustomEvent('km:fbt:timing', { detail }));
      this.track('km_fbt_timing', null, { timing_phase: phase, duration_ms: detail.durationMs, timing_status: status });
    }
    openDrawer(result, refreshOnly = false) {
      const started = scope.performance?.now?.() ?? Date.now();
      let timer, resolveRendered;
      const rendered = new Promise(resolve => { resolveRendered = resolve; });
      const done = event => {
        if (event?.detail?.location && event.detail.location !== 'cart-drawer') return;
        document.removeEventListener('cart:rendered', done);
        scope.clearTimeout(timer);
        this.recordTiming('drawer-render', started, event ? 'ok' : 'timeout');
        resolveRendered();
      };
      document.addEventListener('cart:rendered', done);
      timer = scope.setTimeout(() => done(null), 5000);
      try {
        if (refreshOnly) { document.dispatchEvent(new CustomEvent('cart:build')); document.dispatchEvent(new CustomEvent('cart:open')); }
        else if (typeof scope.KMCartDrawerProductBridge?.notifyAdded === 'function') scope.KMCartDrawerProductBridge.notifyAdded(result, this.addButton);
        else document.dispatchEvent(new CustomEvent('ajaxProduct:added', { detail: { product: result, addToCartBtn: this.addButton } }));
      } catch (_) {
        document.removeEventListener('cart:rendered', done); scope.clearTimeout(timer);
        this.recordTiming('drawer-render', started, 'error');
        // Paid items already exist; an integration failure must never resubmit them.
        this.message.textContent = this.s.giftError;
        resolveRendered();
      }
      return rendered;
    }
    bindPrewarm() {
      if (!this.prewarmStock) return;
      for (const event of ['pointerenter', 'pointerdown', 'focusin']) {
        this.removeEventListener(event, this.prewarmStock);
        this.addEventListener(event, this.prewarmStock, { passive: true });
      }
    }
    disconnectedCallback() {
      for (const event of ['pointerenter', 'pointerdown', 'focusin']) this.removeEventListener(event, this.prewarmStock);
      this.viewObserver?.disconnect();
      this.promotions?.destroy?.();
      scope.clearInterval(this.poll);
      this.form?.removeEventListener('change', this.handleChange);
    }
    findForm() {
      const id = this.dataset.formId;
      if (id) {
        const exact = document.getElementById(id);
        return exact?.matches('form') && exact.querySelector('[name="id"]') ? exact : null;
      }
      const area = this.closest('.shopify-section') || this.parentElement;
      const valid = [...area.querySelectorAll('form')].filter(f => {
        const control = f.querySelector('[name="id"]');
        return control && this.config.main.variants.some(v => String(v.id) === String(control.value));
      });
      return valid.length === 1 ? valid[0] : null;
    }
    el(tag, cls, text) {
      const node = document.createElement(tag);
      if (cls) node.className = cls;
      if (text != null) node.textContent = text;
      return node;
    }
    render() {
      this.append(this.el('h3', 'km-fbt__title', this.s.title));
      this.items.forEach(item => {
        const card = this.el('article', 'km-fbt__card'); item.card = card;
        const header = this.el('div', 'km-fbt__header');
        const check = this.el('input', 'km-fbt__check'); check.type = 'checkbox'; check.checked = item.selected; check.disabled = !item.live;
        check.setAttribute('aria-label', item.product.title);
        check.addEventListener('change', () => { item.selected = check.checked; this.update(); this.track('km_fbt_product_selected', item, { selection_state: item.selected ? 'selected' : 'removed' }); });
        header.append(check); item.check = check;
        if (item.product.image) {
          const img = this.el('img', 'km-fbt__image'); img.src = item.product.image; img.alt = item.product.title; img.loading = 'lazy'; img.width = 72; img.height = 72; header.append(img);
        }
        const name = this.el('a', 'km-fbt__name', item.product.title);
        name.title = item.product.title;
        name.setAttribute('aria-label', item.product.title);
        name.addEventListener('click', () => this.track('km_fbt_view_details_click', item));
        // Only same-origin product paths/URLs are navigable.
        try { const url = new URL(item.product.url, scope.location.origin); if (url.origin === scope.location.origin) name.href = url.href; } catch (_) {}
        header.append(name);
        item.price = this.el('div', 'km-fbt__price'); header.append(item.price); card.append(header);
        item.buttons = [];
        if ((item.product.options || []).some(option => !isDefaultOption(option, item.product))) {
          item.optionsPanel = this.el('details', 'km-fbt__option-picker');
          item.optionsSummary = this.el('summary', 'km-fbt__option-summary', this.s.chooseOptionsEntry);
          item.optionsSummary.addEventListener('click', () => {
            if (!item.optionsPanel.open) this.track('km_fbt_options_open', item, { item_role: 'addon' });
          });
          item.optionsPanel.append(item.optionsSummary);
          card.append(item.optionsPanel);
        }
        (item.product.options || []).forEach((option, index) => {
          if (isDefaultOption(option, item.product)) return;
          const group = this.el('fieldset', 'km-fbt__options');
          group.append(this.el('legend', '', option.name));
          const row = this.el('div', 'km-fbt__option-row');
          option.values.forEach(value => {
            const button = this.el('button', 'km-fbt__option', value); button.type = 'button';
            button.addEventListener('click', () => {
              item.choices[index] = value;
              for (let i = index + 1; i < item.choices.length; i++) {
                if (item.choices[i] && !optionsAvailability(item.product.variants, item.choices, i, item.choices[i])) item.choices[i] = null;
              }
              this.update();
              this.track('km_fbt_option_selected', item, { option_name: option.name, option_value: value, item_role: 'addon' });
              if (item.variant && item.optionsPanel) {
                item.optionsPanel.open = false;
                item.optionsSummary.focus();
              }
            });
            item.buttons.push({ button, index, value }); row.append(button);
          });
          group.append(row); item.optionsPanel.append(group);
        });
        item.status = this.el('p', 'km-fbt__item-status'); card.append(item.status); this.append(card);
      });
      this.promotions = scope.KmFbtPromotions ? new scope.KmFbtPromotions(this, { isPurchasable, resolveVariant, quotedPrice }) : null;
      this.message = this.el('p', 'km-fbt__message'); this.message.setAttribute('role', 'status'); this.message.setAttribute('aria-live', 'polite'); this.append(this.message);
      this.mainStatus = this.el('p', 'km-fbt__item-status'); this.append(this.mainStatus);
      this.addButton = this.el('button', 'km-fbt__add', this.s.add); this.addButton.type = 'button';
      this.addButton.addEventListener('click', () => this.add()); this.append(this.addButton); this.update();
    }
    mainVariant() {
      const id = this.form?.querySelector('[name="id"]')?.value;
      return this.config.main.variants.find(v => String(v.id) === String(id));
    }
    mainIdentity() {
      if (!this.form) return '';
      const data = new FormData(this.form);
      return JSON.stringify([...data.entries()].filter(([key]) => key === 'id' || key === 'quantity' || key === 'selling_plan' || key.startsWith('properties[')).map(([key, value]) => [key, typeof value === 'string' ? value : `${value.name}:${value.size}:${value.lastModified}`]).sort(([a], [b]) => a.localeCompare(b)));
    }
    attemptKey() { return `km-fbt-pending-v2:${this.config.main.id}`; }
    restoreAttempt() {
      try {
        const saved = JSON.parse(scope.sessionStorage?.getItem(this.attemptKey()) || 'null');
        if (saved?.id && Array.isArray(saved.items) && saved.items.length) { this.pendingAttempt = saved; this.receiptId = saved.id; }
      } catch (_) {}
    }
    saveAttempt(attempt) {
      this.pendingAttempt = attempt;
      try {
        if (attempt) scope.sessionStorage?.setItem(this.attemptKey(), JSON.stringify(attempt));
        else scope.sessionStorage?.removeItem(this.attemptKey());
      } catch (_) {}
    }
    async reconcileAttempt() {
      const attempt = this.pendingAttempt;
      if (!attempt) return false;
      const root = (scope.Shopify?.routes?.root || this.config.root || '/').replace(/\/?$/, '/');
      const cart = await requestData(`${root}cart.js?_bogos-ignore`, { cache: 'no-store', headers: { Accept: 'application/json' } });
      const received = (cart.items || []).filter(line => line.properties?._km_fbt_request === attempt.id);
      const complete = attempt.items.every(item => received.filter(line => String(line.variant_id) === String(item.id)).reduce((sum, line) => sum + line.quantity, 0) >= (item.baseline || 0) + item.quantity);
      if (complete) {

        this.saveAttempt(null);
        this.track('km_fbt_add_success', null, { paid_line_count: attempt.items.length, recovered: 'true', includes_main: String(attempt.includesMain) });
        this.message.textContent = this.s.added;
        this.openDrawer(null, true);
        return true;
      }
      this.openDrawer(null, true);
      // A completed server rejection is safe to retry only after showing any partial cart mutation.
      const mutated = attempt.items.some(item => received.filter(line => String(line.variant_id) === String(item.id)).reduce((sum, line) => sum + line.quantity, 0) > (item.baseline || 0));
      if (attempt.rejected && !mutated) { this.saveAttempt(null); this.message.textContent = this.s.rejected; }
      else this.message.textContent = this.s.uncertain;
      return false;
    }
    quantity() {
      const raw = this.form?.elements?.namedItem('quantity')?.value || '1';
      const quantity = Number(raw);
      return Number.isSafeInteger(quantity) && quantity > 0 ? quantity : null;
    }
    displayPrice(node, price, coupon) {
      node.replaceChildren(); node.removeAttribute('title'); const offer = this.quotedPrice(price, coupon);
      if (offer.discount > 0) {
        node.append(this.el('s', 'km-fbt__base', this.money(offer.basePrice)));
        node.append(this.el('strong', '', this.money(offer.price)));
        node.append(this.el('span', 'km-fbt__badge', offer.type === 'DISCOUNT_PERCENT' ? this.s.offPercent.replace('{value}', String(offer.value)) : this.s.offAmount.replace('{value}', this.money(offer.discount))));
        node.title = `${coupon?.perItem === false && offer.type === 'DISCOUNT_AMOUNT' ? this.s.soloPrice : this.s.estimated}: ${offer.code}`;
      } else node.append(this.el('strong', '', this.money(offer.basePrice)));
      return offer;
    }
    quotedPrice(price, coupon) {
      return quotedPrice(price, coupon);
    }
    update() {
      if (!this.addButton) return;
      const main = this.mainVariant(); const qty = this.quantity();
      const mainReady = Boolean(this.form && isPurchasable(main) && qty && (main.inventoryPolicy === 'continue' || main.tracked !== true || main.quantity >= qty));
      let ready = mainReady;
      const includeMain = true;
      this.mainStatus.textContent = mainReady ? '' : this.s.mainUnavailable;
      this.items.forEach(item => { item.variant = resolveVariant(item.product.variants, item.choices); });
      this.items.forEach(item => {
        item.variant = resolveVariant(item.product.variants, item.choices);
        if (item.optionsSummary) {
          const label = item.variant ? item.variant.options.join(' / ') : this.s.chooseOptionsEntry;
          item.optionsSummary.textContent = label;
          item.optionsSummary.setAttribute('aria-label', `${this.s.chooseOptionsEntry}: ${item.variant ? label : item.product.title}`);
        }
        const possible = item.product.variants.filter(v => isPurchasable(v) && v.options.every((x, i) => !item.choices[i] || x === item.choices[i]));
        const shown = item.variant || possible.sort((a, b) => a.price - b.price)[0] || item.product.variants[0];
        this.displayPrice(item.price, shown?.price || 0, this.promotions ? this.promotions.priceCoupon(item.product) : item.product.coupon);
        item.card.classList.toggle('needs-options', item.selected && !isPurchasable(item.variant));
        item.card.classList.toggle('is-selected', item.selected); item.card.classList.toggle('is-sold-out', !item.live);
        item.card.hidden = !item.live;
        item.check.disabled = this.busy || !item.live; item.check.checked = item.selected;
        item.buttons.forEach(({ button, index, value }) => {
          button.disabled = this.busy || !optionsAvailability(item.product.variants, item.choices, index, value);
          button.setAttribute('aria-pressed', String(item.choices[index] === value));
        });
        item.status.textContent = !item.live ? this.s.soldOut : !item.variant ? '' : !isPurchasable(item.variant) ? this.s.unavailable : '';
        if (item.selected) {
          ready = ready && isPurchasable(item.variant);
        }
      });
      const promotionReady = !this.promotions || this.promotions.isReady();
      const hasSelection = includeMain || this.items.some(item => item.selected) || this.promotions?.choices?.some(choice => choice.selected);
      this.readyToAdd = ready && promotionReady && hasSelection;
      this.firstPendingItem = this.items.find(item => item.selected && !isPurchasable(item.variant) && item.optionsPanel);
      this.addButton.disabled = this.busy || this.paidAdded === true || !mainReady || !hasSelection || (!ready && !this.firstPendingItem);
      this.addButton.textContent = this.busy ? this.s.adding : this.pendingAttempt ? this.s.checkCart : !hasSelection ? this.s.nothingSelected : !this.readyToAdd ? this.s.completeOptions : this.s.add;
      if (this.pendingAttempt) this.addButton.disabled = Boolean(this.busy);
      this.addButton.title = this.readyToAdd ? '' : !isPurchasable(main) ? this.s.mainUnavailable : this.s.chooseOptions;
    }
    async loadFreshConfig() {
      if (this.stockCache && Date.now() - this.stockCache.at < 10000) return this.stockCache.data;
      if (this.stockRequest) return this.stockRequest;
      const work = (async () => {
      if (this.config.sectionId !== 'km-heated-fbt-data') throw new Error('section');
      const url = new URL(this.config.main.url, scope.location.origin);
      if (url.origin !== scope.location.origin) throw new Error('origin');
      url.searchParams.set('section_id', this.config.sectionId);
      if (this.config.previewThemeId) url.searchParams.set('preview_theme_id', String(this.config.previewThemeId));
      const text = await requestData(url.href, { cache: 'no-store', headers: { Accept: 'text/html' } }, 'text', 8000);
      const html = new DOMParser().parseFromString(text, 'text/html');
      let fresh = null;
      for (const script of html.querySelectorAll('script[data-fbt-config]')) {
        try {
          const candidate = JSON.parse(script.textContent);
          if (String(candidate.main?.id) === String(this.config.main.id) && Array.isArray(candidate.main?.variants) && Array.isArray(candidate.items)) { fresh = candidate; break; }
        } catch (_) {}
      }
      if (!fresh) throw new Error('config');
      this.stockCache = { data: fresh, at: Date.now() };
      return fresh;
      })();
      this.stockRequest = work;
      try { return await work; } finally { if (this.stockRequest === work) this.stockRequest = null; }
    }
    async refreshStock() {
      const fresh = await this.loadFreshConfig();
      const oldMain = this.mainVariant();
      let decision = 'ready';
      const selected = this.items.filter(item => item.selected).map(item => ({item, variant: resolveVariant(item.product.variants, item.choices), coupon: item.product.coupon}));
      this.config.main = fresh.main;
      const currentMain = this.mainVariant();
      decision = freshnessDecision({variant: oldMain, coupon: this.previousMainCoupon}, {variant: currentMain, coupon: fresh.main.coupon}, this.quantity() || 1);
      for (const item of this.items) {
        const match = fresh.items.find(x => String(x.product?.id) === String(item.product.id));
        item.product = match?.product || { ...item.product, variants: [] };
        item.live = item.product.variants.some(isPurchasable);
        if (!item.live) item.selected = false;
      }
      for (const previous of selected) {
        const status = freshnessDecision(previous, { variant: resolveVariant(previous.item.product.variants, previous.item.choices), coupon: previous.item.product.coupon });
        if (status === 'unavailable' || decision === 'ready') decision = status;
      }
      if (this.promotions) {
        const status = await this.promotions.refresh(fresh) || 'ready';
        if (status === 'unavailable' || decision === 'ready') decision = status;
      }
      return decision;
    }
    async add() {
      if (this.busy || this.paidAdded) return;
      if (this.pendingAttempt) {
        this.busy = true; this.update();
        try { await this.reconcileAttempt(); } catch (_) { this.message.textContent = this.s.uncertain; }
        finally { this.busy = false; this.update(); }
        return;
      }
      this.update();
      if (this.addButton.disabled) return;
      this.track('km_fbt_add_click', null, { selected_count: this.items.filter(item => item.selected).length, options_complete: String(this.readyToAdd) });
      if (!this.readyToAdd) {
        const item = this.firstPendingItem;
        if (item) {
          item.optionsPanel.open = true;
          item.optionsSummary.focus();
          item.optionsPanel.scrollIntoView({block: 'center', behavior: 'smooth'});
        } else this.promotions?.openPending();
        return;
      }
      if (typeof this.form.checkValidity === 'function' && !this.form.checkValidity()) {
        this.form.reportValidity();
        return;
      }
      const initialIdentity = this.mainIdentity();
      const initialMainId = this.mainVariant()?.id;
      this.previousMainCoupon = this.config.main.coupon;
      this.busy = true; this.message.textContent = ''; this.update();
      let freshDecision;
      const refreshStarted = scope.performance?.now?.() ?? Date.now();
      try { freshDecision = await this.refreshStock(); this.recordTiming('stock-refresh', refreshStarted); }
      catch (_) { this.track('km_fbt_add_error', null, { error_stage: 'stock_refresh', paid_status: 'not_submitted' }); this.recordTiming('stock-refresh', refreshStarted, 'error'); this.message.textContent = this.s.refreshError; this.busy = false; this.update(); return; }
      this.busy = false; this.update();
      if (freshDecision !== 'ready') {
        this.track('km_fbt_add_error', null, { error_stage: 'stock_changed', paid_status: 'not_submitted' });
        this.message.textContent = freshDecision === 'priceChanged' ? this.s.priceChanged : this.s.unavailable;
        return;
      }
      if (this.mainVariant()?.id !== initialMainId || this.mainIdentity() !== initialIdentity || !this.readyToAdd) { this.message.textContent = this.s.chooseOptions; return; }
      if (typeof this.form.checkValidity === 'function' && !this.form.checkValidity()) { this.form.reportValidity(); return; }
      const main = this.mainVariant(); const quantity = this.quantity();
      if (!isPurchasable(main) || !quantity) return;
      const data = new FormData(this.form); const properties = Object.create(null);
      for (const [key, value] of data.entries()) {
        const match = key.match(/^properties\[([^\]]+)\]$/);
        if (match && typeof value === 'string') properties[match[1]] = value;
        // File properties cannot be safely serialized to the cart JSON endpoint.
        if (match && typeof value !== 'string' && value.size > 0) { this.message.textContent = this.s.error; return; }
      }
      const first = { id: main.id, quantity, properties };
      const plan = data.get('selling_plan'); if (plan) first.selling_plan = String(plan);
      const includeMain = true;
      const items = includeMain ? [first] : [];
      for (const item of this.items.filter(x => x.selected)) {
        const variant = resolveVariant(item.product.variants, item.choices);
        if (!isPurchasable(variant)) { this.message.textContent = this.s.chooseOptions; return; }
        items.push({ id: variant.id, quantity: 1 });
      }
      this.busy = true; this.message.textContent = ''; this.update();
      const prefix = (scope.Shopify?.routes?.root || this.config.root || '/').replace(/\/?$/, '/');
      const endOperation = scope.theme?.cart?.beginAddOperation?.('heated-fbt') || (() => {});
      let operationFinished = false;
      const finishOperation = () => { if (!operationFinished) { operationFinished = true; endOperation(true); } };
      const postStarted = scope.performance?.now?.() ?? Date.now();
      let paidConfirmed = items.length === 0;
      try {
        if (items.length) {
          const id = scope.crypto?.randomUUID?.() || `${Date.now()}-${Math.random().toString(36).slice(2)}`;
          for (const item of items) item.properties = { ...item.properties, _km_fbt_request: id };
          this.saveAttempt({ id, items: items.map(item => ({ id: item.id, quantity: item.quantity, baseline: 0 })), includesMain: includeMain, identity: initialIdentity });
          let result;
          try {
            result = await requestData(`${prefix}cart/add.js${this.promotions?.usesNativeGiftFlow?.() ? '?_bogos-ignore' : ''}`, { method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify({ items }) }, 'json', 15000);
            if (!result || !Array.isArray(result.items)) throw new Error('cart-response');
          } catch (error) {
            this.saveAttempt({ ...this.pendingAttempt, rejected: [400, 404, 422].includes(error.status) });
            this.track('km_fbt_add_error', null, { error_stage: 'paid_post', paid_status: 'unconfirmed', http_status: error.status || 0 });
            this.recordTiming('paid-post', postStarted, 'error');
            this.stockCache = null;
            finishOperation();
            await this.reconcileAttempt();
            return;
          }
          paidConfirmed = true;
          this.stockCache = null;
          this.saveAttempt(null);

          this.track('km_fbt_add_success', null, { paid_line_count: items.length, paid_quantity: items.reduce((sum, item) => sum + item.quantity, 0), includes_main: String(includeMain) });
          this.recordTiming('paid-post', postStarted);
          this.paidAdded = true;
          // Notify the drawer immediately; gift processing is a separate, bounded stage.
          this.openDrawer(result);
          finishOperation();
        } else finishOperation();
        const giftStarted = scope.performance?.now?.() ?? Date.now();
        let giftFailure = null;
        try {
          await this.promotions?.addGift();
          if (this.promotions?.choices?.some(choice => choice.selected)) {
            this.openDrawer(null, true);
          }
        } catch (error) { giftFailure = error; this.openDrawer(null, true); }
        this.recordTiming('gift-add', giftStarted, giftFailure ? 'error' : 'ok');
        if (this.promotions?.choices?.some(choice => choice.selected)) this.track('km_fbt_gift_result', null, { gift_status: giftFailure ? 'unconfirmed' : 'confirmed' });
        this.message.textContent = giftFailure ? this.s.giftError : this.s.added;
      } catch (_) {
        this.message.textContent = paidConfirmed ? this.s.giftError : this.s.uncertain;
      } finally {
        // This finishes this module's operation only; it does not clear another module's failures.
        finishOperation();
        this.busy = false;
        this.paidAdded = false;
        this.update();
      }
    }
  }
  scope.customElements.define('km-heated-fbt', HeatedFbt);
})(typeof window !== 'undefined' ? window : globalThis);
